import React, { useState, useEffect } from "react";
import { backupAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Database,
  Download,
  Loader2,
  RefreshCw,
  RotateCcw,
  Trash2,
  Clock,
  Calendar,
  CheckCircle,
  AlertTriangle,
  HardDrive,
} from "lucide-react";

export function BackupPage() {
  const [backups, setBackups] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [message, setMessage] = useState(null);

  const [formData, setFormData] = useState({
    enabled: true,
    frequency: "daily",
    retention_days: 30,
    time_hour: 2,
  });

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [backupsRes, settingsRes] = await Promise.all([
        backupAPI.getAll(),
        backupAPI.getSettings(),
      ]);
      setBackups(backupsRes.data);
      setSettings(settingsRes.data);
      setFormData({
        enabled: settingsRes.data.enabled,
        frequency: settingsRes.data.frequency,
        retention_days: settingsRes.data.retention_days,
        time_hour: settingsRes.data.time_hour,
      });
    } catch (error) {
      console.error("Error loading backup data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      await backupAPI.create();
      showMessage("Backup creato con successo");
      loadData();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante la creazione del backup", "error");
    } finally {
      setCreating(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await backupAPI.updateSettings(formData);
      showMessage("Impostazioni salvate");
      loadData();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante il salvataggio", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (backup) => {
    setDownloading(backup.id);
    try {
      const response = await backupAPI.download(backup.id);
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${backup.filename}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showMessage("Errore durante il download", "error");
    } finally {
      setDownloading(null);
    }
  };

  const handleRestore = async (backup) => {
    const confirmText = `ATTENZIONE: Questa operazione sovrascriverà TUTTI i dati attuali con quelli del backup "${backup.filename}".\n\nQuesta azione è IRREVERSIBILE.\n\nDigita "CONFERMA" per procedere:`;
    const userInput = window.prompt(confirmText);
    
    if (userInput !== "CONFERMA") {
      if (userInput !== null) {
        showMessage("Ripristino annullato: testo di conferma non corretto", "error");
      }
      return;
    }

    setRestoring(backup.id);
    try {
      await backupAPI.restore(backup.id);
      showMessage("Backup ripristinato con successo. La pagina verrà ricaricata.");
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante il ripristino", "error");
      setRestoring(null);
    }
  };

  const handleDelete = async (backup) => {
    if (!window.confirm(`Eliminare il backup "${backup.filename}"?`)) {
      return;
    }

    try {
      await backupAPI.delete(backup.id);
      showMessage("Backup eliminato");
      loadData();
    } catch (error) {
      showMessage("Errore durante l'eliminazione", "error");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatSize = (size) => {
    if (!size) return "-";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="backup-page">
      {/* Message Banner */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === "error" 
            ? "bg-red-500 text-white" 
            : "bg-emerald-500 text-white"
        }`}>
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <Database size={32} className="text-[#2D7BB7]" />
            Backup Database
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestisci i backup automatici e manuali del database
          </p>
        </div>
        <Button
          onClick={handleCreateBackup}
          disabled={creating}
          className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
          data-testid="create-backup-btn"
        >
          {creating ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <HardDrive className="mr-2" size={18} />
          )}
          Crea Backup Ora
        </Button>
      </div>

      {/* Settings Card */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
            <Clock size={20} className="text-[#2D7BB7]" />
            Backup Automatico
          </CardTitle>
          <CardDescription>
            Configura la frequenza e la retention dei backup automatici
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveSettings} className="space-y-6">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <Label className="text-foreground font-medium">Abilita Backup Automatico</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  I backup verranno creati automaticamente secondo la frequenza impostata
                </p>
              </div>
              <Switch
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
                data-testid="backup-enabled-switch"
              />
            </div>

            {/* Frequency */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Frequenza</Label>
                <Select
                  value={formData.frequency}
                  onValueChange={(val) => setFormData({ ...formData, frequency: val })}
                >
                  <SelectTrigger className="bg-background border-input" data-testid="backup-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Giornaliero</SelectItem>
                    <SelectItem value="weekly">Settimanale (Domenica)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">Ora del giorno</Label>
                <Select
                  value={String(formData.time_hour)}
                  onValueChange={(val) => setFormData({ ...formData, time_hour: parseInt(val) })}
                >
                  <SelectTrigger className="bg-background border-input" data-testid="backup-hour-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {i.toString().padStart(2, "0")}:00
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">Retention (giorni)</Label>
                <Select
                  value={String(formData.retention_days)}
                  onValueChange={(val) => setFormData({ ...formData, retention_days: parseInt(val) })}
                >
                  <SelectTrigger className="bg-background border-input" data-testid="backup-retention-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 giorni</SelectItem>
                    <SelectItem value="14">14 giorni</SelectItem>
                    <SelectItem value="30">30 giorni</SelectItem>
                    <SelectItem value="60">60 giorni</SelectItem>
                    <SelectItem value="90">90 giorni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Status Info */}
            {settings && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-blue-600" />
                  <span className="text-sm text-blue-800">
                    <strong>Ultimo backup:</strong> {formatDate(settings.last_backup)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-blue-600" />
                  <span className="text-sm text-blue-800">
                    <strong>Prossimo backup:</strong> {settings.enabled ? formatDate(settings.next_backup) : "Disabilitato"}
                  </span>
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
                data-testid="save-backup-settings-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Salva Impostazioni"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Backups List */}
      <Card className="bg-card border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
                <HardDrive size={20} className="text-[#2D7BB7]" />
                Backup Disponibili
              </CardTitle>
              <CardDescription>
                {backups.length} backup salvati
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadData}
              className="border-border"
              data-testid="refresh-backups-btn"
            >
              <RefreshCw size={16} className="mr-2" />
              Aggiorna
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {backups.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Database size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nessun backup disponibile</p>
              <p className="text-sm mt-1">Crea il primo backup cliccando il pulsante in alto</p>
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map((backup) => (
                <div
                  key={backup.id}
                  className="flex items-center justify-between p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                  data-testid={`backup-item-${backup.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${
                      backup.type === "scheduled" 
                        ? "bg-blue-100 text-blue-600" 
                        : "bg-emerald-100 text-emerald-600"
                    }`}>
                      {backup.type === "scheduled" ? (
                        <Clock size={20} />
                      ) : (
                        <HardDrive size={20} />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{backup.filename}</p>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>{formatDate(backup.created_at)}</span>
                        <span>•</span>
                        <span>{formatSize(backup.size)}</span>
                        <span>•</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          backup.type === "scheduled"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-emerald-100 text-emerald-700"
                        }`}>
                          {backup.type === "scheduled" ? "Automatico" : "Manuale"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(backup)}
                      disabled={downloading === backup.id}
                      className="border-border"
                      data-testid={`download-backup-${backup.id}`}
                    >
                      {downloading === backup.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Download size={16} />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRestore(backup)}
                      disabled={restoring === backup.id}
                      className="border-amber-300 text-amber-600 hover:bg-amber-50"
                      data-testid={`restore-backup-${backup.id}`}
                    >
                      {restoring === backup.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RotateCcw size={16} />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(backup)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-backup-${backup.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Warning Card */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium mb-1">Importante</p>
              <ul className="list-disc list-inside space-y-1 text-amber-700">
                <li>Il ripristino di un backup sovrascrive TUTTI i dati attuali</li>
                <li>Assicurati di avere un backup recente prima di eseguire operazioni critiche</li>
                <li>I backup automatici vengono eliminati dopo il periodo di retention</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
